import React, { useState, useEffect } from 'react';
import { Button, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import ReactCodeInput from 'react-code-input';
import Cookies from 'universal-cookie';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import axios from 'axios';
import { v4 as uuidV4 } from 'uuid';
import { jwtDecode } from 'jwt-decode';
import { GoogleLogin } from '@react-oauth/google';
import { ENCRYPT_DECRYPT_KEY } from '../../constants/environment';
import ShowProgress from '../ui/Backdrop';
import { encryptAESJSONData, ErrorToast } from '../utils/Utils';
import { apiEndpoints } from '../../constants/apiEndpoints';

toast.configure();

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiTextField-root': {
      margin: theme.spacing(1),
      width: 200,
    },
  },
  heading_text: {
    fontWeight: 600,
    fontSize: '36px',
    lineHeight: '44px',
    fontStyle: 'normal',
    marginBottom: '.8em',
  },
  heading_desc: {
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '20px',
  },
  resendOtp: {
    justifyContent: 'end',
    width: '100%',
  },
}));

const COOKIE_ATTR = {
  path: '/',
  maxAge: 604800,
  sameSite: true,
  secure: true,
};

const LoginPage = () => {
  const classes = useStyles();
  const cookies = new Cookies();
  const isProduction = process.env.REACT_APP_NODE_ENV === 'production';
  const [isValid, setIsValid] = useState(false);
  const [userName, setUserName] = useState();
  const [errorMessage, setErrorMessage] = React.useState('');
  const [showProgress, setShowProgress] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [isOtpValid, setIsOtpValid] = useState(true);
  const [otpCode, setOtp] = useState('');

  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    if (showOtp && otpCode.length === 6 && !isVerifying) {
      setIsVerifying(true);
      verifyOtp();
    }
  }, [otpCode]);

  const authExpireAt = (expirySec) => {
    const date = new Date();
    return format(new Date(date.setSeconds(date.getSeconds() + parseInt(expirySec, 10) - 20)), 'yyyy/MM/dd hh:mm a');
  };
  const sendOtp = () => {
    setShowProgress(true);

    const data = {
      email: userName,
    };
    const encryptedData = { data: encryptAESJSONData(data, ENCRYPT_DECRYPT_KEY) };
    const config = {
      method: 'post',
      url: apiEndpoints.AUTH_LOGIN,
      headers: {
        'Content-Type': 'application/json',
      },
      data: encryptedData,
    };

    axios(config)
      .then((response) => {
        setShowProgress(false);
        if (response.status === 200) {
          setShowOtp(true);
        }
      })
      .catch((error) => {
        setShowProgress(false);
        setErrorMessage(error.response.data.errors[0].msg);
        setIsValid(true);
      });
  };
  const verifyOtp = () => {
    const transactionId = uuidV4();

    setShowProgress(true);
    setIsVerifying(true);
    const data = {
      email: userName,
      otp: otpCode,
      transactionId,
    };
    const encryptedData = { data: encryptAESJSONData(data, ENCRYPT_DECRYPT_KEY) };
    const config = {
      method: 'post',
      url: apiEndpoints.AUTH_LOGIN_VERIFY,
      headers: {
        'Content-Type': 'application/json',
      },
      data: encryptedData,
    };

    axios(config)
      .then((response) => {
        setShowProgress(false);
        setIsVerifying(false);
        if (response.status === 200) {
          const decodedAccessToken = jwtDecode(response.data.result.accessToken);
          const decodedRefreshToken = jwtDecode(response.data.result.refreshToken);

          if (
            transactionId !== decodedAccessToken.transactionId ||
            transactionId !== decodedRefreshToken.transactionId
          ) {
            ErrorToast('Please login again. Something went wrong.');
            return;
          }

          const expiresAt = authExpireAt(response.headers.expires);
          cookies.set('access_token', response.data.result.accessToken, {
            ...COOKIE_ATTR,
          });
          cookies.set('refresh_token', response.data.result.refreshToken, {
            ...COOKIE_ATTR,
          });
          cookies.set('auth_expires', expiresAt, {
            ...COOKIE_ATTR,
          });
          cookies.set('media_token', response.data.result.mediaToken, {
            ...COOKIE_ATTR,
          });

          window.location.pathname = '/dashboard';
        }
      })
      .catch((error) => {
        setIsVerifying(false);
        ErrorToast(error);
        setIsOtpValid(false);
        setShowProgress(false);
      });
  };
  const handleGoogleSuccess = async ({ credential }) => {
    setShowProgress(true);
    try {
      const response = await axios.post(apiEndpoints.AUTH_GOOGLE_LOGIN, { credential });
      if (response.status === 200) {
        const expiresAt = authExpireAt(response.headers.expires);
        cookies.set('access_token', response.data.result.accessToken, { ...COOKIE_ATTR });
        cookies.set('refresh_token', response.data.result.refreshToken, { ...COOKIE_ATTR });
        cookies.set('auth_expires', expiresAt, { ...COOKIE_ATTR });
        cookies.set('media_token', response.data.result.mediaToken, { ...COOKIE_ATTR });
        window.location.pathname = '/dashboard';
      }
    } catch (error) {
      ErrorToast(error);
    } finally {
      setShowProgress(false);
    }
  };

  const handleChange = (event) => {
    setUserName(event.target.value);
    setIsValid(false);
  };
  const handleOtpChange = (value) => {
    setOtp(value);
  };
  const codeStyle = {
    inputStyle: {
      fontFamily: 'monospace',
      margin: '4px',
      width: '36px',
      borderRadius: '6px',
      fontSize: '32px',
      height: '42px',
      paddingLeft: '8px',
      backgroundColor: 'white',
      color: 'black',
      boxShadow: 'rgb(0 0 0 / 10%) 0px 0px 10px 0px',
      boxSizing: 'border-box',
      border: '1px solid #622FEE',
    },
    inputStyleInvalid: {
      fontFamily: 'monospace',
      margin: '4px',
      width: '36px',
      borderRadius: '6px',
      fontSize: '32px',
      height: '42px',
      paddingLeft: '8px',
      backgroundColor: 'white',
      color: 'black',
      boxShadow: 'rgb(0 0 0 / 10%) 0px 0px 10px 0px',
      boxSizing: 'border-box',
      border: '1px solid red',
    },
  };
  return (
    <Grid container>
      <Grid item xs={12}>
        <p className={classes.heading_text}>Login</p>
        {!isProduction && (
          <p className={classes.heading_desc} style={{ marginBottom: '0px', marginTop: '0px' }}>
            Please enter your {showOtp ? 'OTP' : 'Email ID'}
          </p>
        )}
        {!isProduction && !showOtp && (
          <TextField
            id="outlined-error-helper-text1"
            label="xxx@rocked.us"
            defaultValue=""
            value={userName}
            placeholder="xxx@rocked.us"
            variant="outlined"
            fullWidth
            error={isValid}
            helperText={errorMessage}
            margin="normal"
            onChange={handleChange}
          />
        )}
        {!isProduction && showOtp && (
          <ReactCodeInput
            id="otpCode"
            type="string"
            isValid={isOtpValid}
            fields={6}
            onChange={handleOtpChange}
            value={otpCode}
            {...codeStyle}
          />
        )}

        {!isProduction && (
          <Button
            variant="contained"
            color="primary"
            style={{
              marginTop: '18px',
              width: '100%',
              textDecoration: 'none',
            }}
            onClick={showOtp ? verifyOtp : sendOtp}
          >
            {showOtp ? 'Submit' : 'Next'}
          </Button>
        )}

        {!isProduction && showOtp && (
          <Button variant="text" color="primary" className={classes.resendOtp} onClick={sendOtp}>
            Resend OTP
          </Button>
        )}

        {(isProduction || !showOtp) && (
          <>
            {!isProduction && (
              <p style={{ textAlign: 'center', margin: '16px 0 8px', color: '#888', fontSize: '13px' }}>or</p>
            )}
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => ErrorToast('Google sign-in failed. Please try again.')}
              width="400"
            />
          </>
        )}
      </Grid>
      {showProgress ? <ShowProgress /> : null}
    </Grid>
  );
};

export default LoginPage;
